import React, { useState, useEffect, useRef } from "react";
import ItemDetailsForms from "./ItemDetailsForms";
import DetailUserDefinedTab from "./tabs/DetailUserDefinedTab";
import DetailItemsTable from "./DetailItemsTable";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function DetailTabs({ initialValues, onDetailsChange, readOnly = false }) {


  const [activeTab, setActiveTab] = useState(0);
  const [detailsForm, setDetailsForm] = useState({});
  const [userDefinedForm, setUserDefinedForm] = useState({});
  const [tableData, setTableData] = useState([]);
  const [editId, setEditId] = useState(null);
  const [itemNumber, setItemNumber] = useState(1);
  const tableWrapRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  console.log("DetailTabs initialValues:", initialValues);

  const tabs = [
    { label: "Item Detail", component: ItemDetailsForms },
    // { label: "User Defined", component: DetailUserDefinedTab },
  ];

  // Load initial values
  useEffect(() => {
    const incomingItems =
      initialValues?.detailItems || initialValues?.lineItems || [];

    if (Array.isArray(incomingItems) && incomingItems.length > 0) {

      const normalized = incomingItems.map((it, idx) => {
        // 🔹 Normalize keys first (console.log yahin chalega)
        const base = normalizeItemKeys(it, idx);

        // 🔹 Ensure id
        const id =
          base.id ||
          it.id ||
          it._id ||
          base.lineNo ||
          Date.now() + idx;

        // 🔹 Ensure lineNo
        const lineNo =
          base.lineNo ||
            (base.detail && base.detail.lineNumber)
            ? String(
              base.lineNo ||
              (base.detail && base.detail.lineNumber)
            ).padStart(5, "0")
            : String(idx + 1).padStart(5, "0");

        return { ...base, id, lineNo };
      });

      setTableData(normalized);
      setItemNumber(normalized.length + 1);
    } else {
      setTableData([]);
      setItemNumber(1);
    }
  }, [initialValues]);


  // Normalize item keys
  const normalizeItemKeys = (it, idx = 0) => {
    const src = it || {};

    console.log("Normalizing item:", src);
    const normalized = { ...src };

    for (let i = 1; i <= 5; i++) {
      const apiKey = `poLineUDF${i}`;
      const formKey = `poLineUdf${i}`;
      if (src[formKey] === undefined && src[apiKey] !== undefined) {
        normalized[formKey] = src[apiKey];
      } else if (src[formKey] === undefined) {
        normalized[formKey] = "";
      }
    }


    const detail = src.detail || src.raw?.detail || {};

  
    console.log("Detail for normalization:", detail.totalReceivedQty);

    if (src.createdBy && !src.createByUser) normalized.createByUser = src.createdBy;
    if (src.updatedBy && !src.editByUser) normalized.editByUser = src.updatedBy;
    if (src.createDate && !normalized.createDate) normalized.createDate = src.createDate;
    if (src.editDate && !normalized.editDate) normalized.editDate = src.editDate;
    if (!normalized.createDate && src.createdAt) normalized.createDate = src.createdAt;
    if (!normalized.editDate && src.updatedAt) normalized.editDate = src.updatedAt;

    if (src.notes === undefined && src.udf && src.udf.notes) normalized.notes = src.udf.notes;
    if (normalized.notes === undefined) normalized.notes = src.notes ?? "";

    normalized.purchaseOrder = normalized.purchaseOrder || (initialValues && initialValues.purchaseOrder) || "";
    normalized.lineNo = normalized.lineNo || (normalized.lineNumber ? String(normalized.lineNumber).padStart(5, "0") : String(idx + 1).padStart(5, "0"));
    normalized.item = normalized.skuId ?? normalized.skuId ?? "";
    normalized.description = normalized.description ?? "";
    normalized.orderedQty = normalized.orderedQty ?? 0;
    // normalized.receivedQty = normalized.receivedQty ?? 0;

    normalized.receivedQty =
      detail.totalReceivedQty ??
      detail.receivedQty ??
      src.totalReceivedQty ??
      src.receivedQty ??
      0;
    normalized.uom = normalized.uom ?? "";
    normalized.pack = normalized.pack ?? "";
    normalized.lpnNo = normalized.lpnNo || detail.lpnNo || detail.lpn || "";
    normalized.batchNum = normalized.batchNum || detail.batchNum || detail.batchNo || detail.batchNumber || "";
    normalized.qcRequired = normalized.qcRequired ?? "No";
    normalized.status = normalized.status ?? "New";

    return normalized;
  };

  const handleDetailsNext = (formData) => {
    setDetailsForm(formData);
    setActiveTab(1);
  };

  const handleUserDefinedAddOrUpdate = (formData) => {
    let updatedRows;
    if (editId) {
      updatedRows = tableData.map((row) =>
        row.id === editId ? normalizeItemKeys({ ...detailsForm, ...formData }, 0) : row
      );
      setTableData(updatedRows);
      setEditId(null);
    } else {
      const lineNo = String(itemNumber).padStart(5, "0");
      const combined = { ...detailsForm, ...formData, lineNo, id: Date.now() };
      const normalizedCombined = normalizeItemKeys(combined, tableData.length);
      updatedRows = [...tableData, normalizedCombined];
      setTableData(updatedRows);
      setItemNumber((prev) => prev + 1);

      requestAnimationFrame(() => {
        if (tableWrapRef.current) {
          tableWrapRef.current.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
      });
    }

    // 🔔 Notify parent of add/update
    if (onDetailsChange) {
      onDetailsChange({ detailItems: updatedRows });
    }

    setDetailsForm({});
    setUserDefinedForm({});
    setActiveTab(0);
  };

  const handleCancel = () => {
    setDetailsForm({});
    setUserDefinedForm({});
    setEditId(null);
    setActiveTab(0);
  };

  const handleEditItem = (row) => {
    const {
      poLineUdf1, poLineUdf2, poLineUdf3, poLineUdf4, poLineUdf5,
      createByUser, createDate, editByUser, editDate, notes,
      ...details
    } = row;
    setDetailsForm(details);
    setUserDefinedForm({
      poLineUdf1, poLineUdf2, poLineUdf3, poLineUdf4, poLineUdf5,
      createByUser, createDate, editByUser, editDate, notes
    });
    setEditId(row.id);
    setActiveTab(0);
  };

  const handleDeleteItem = (id) => {
    setSelectedId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    setTableData(prev => {
      const updated = prev.filter(item => item.id !== selectedId);

      // 🔔 Notify parent of deletion
      if (onDetailsChange) {
        onDetailsChange({ detailItems: updated });
      }

      return updated;
    });

    setConfirmOpen(false);
    setSelectedId(null);
  };

  const ActiveComponent = tabs[activeTab].component;

  return (
    <div className="mt-3">
      {!readOnly && (
        <ul className="nav nav-tabs mb-3">
          {tabs.map((tab, index) => (
            <li className="nav-item" key={index}>
              <button
                className={`nav-link ${activeTab === index ? "active" : ""}`}
                onClick={() => setActiveTab(index)}
                type="button"
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div className="p-2">
          <div style={{ display: activeTab === 0 ? "block" : "none" }}>
            <ItemDetailsForms
              poFormData={initialValues}
              initialValues={detailsForm || {}}
              onSubmit={handleUserDefinedAddOrUpdate}
              onCancel={handleCancel}
              isNextButton={!editId}
              isEditMode={!!editId && activeTab === 0}
              itemNumber={itemNumber}
              readOnly={readOnly}
            />
          </div>

          <div style={{ display: activeTab === 1 ? "block" : "none" }}>
            <DetailUserDefinedTab
              initialValues={userDefinedForm || {}}
              onSubmit={handleUserDefinedAddOrUpdate}
              onCancel={handleCancel}
              isAddButton={!editId}
              isEditMode={!!editId}
              readOnly={readOnly}
            />
          </div>
        </div>
      )}

      <div ref={tableWrapRef}>
        <DetailItemsTable
          items={tableData}
          onItemsChange={readOnly ? undefined : setTableData}
          onEditItem={readOnly ? undefined : handleEditItem}
          onDeleteItem={readOnly ? undefined : handleDeleteItem}
          readOnly={readOnly}
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Item?"
        confirmText="Are you sure you want to remove this item?"
        yesText="Yes, Remove"
        noText="Cancel"
      />
    </div>
  );
}
